import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DEFAULT_CURRENCY,
  DEFAULT_LOCALE,
  type BrandingSetting,
  type FiscalProfile,
  type Locale,
} from '@arriba.digital/contracts';
import { settingsApi } from '../api/resources';
import { queryKeys } from '../api/query-keys';
import { useAuth } from '../auth/auth-provider';
import { useCountryProfile } from './use-country-profile';

/**
 * Tenant-scoped settings, cached for the session.
 *
 * Branding rarely changes, so it is fetched once and reused; the currency
 * default keeps every monetary figure correct even before the first fetch
 * resolves. It falls back to the workspace's own country, then to a compiled
 * constant — which used to be the Argentine peso unconditionally, and would
 * have labelled a Chilean workspace's figures in pesos.
 */
export function useTenantSettings(options?: { enabled?: boolean }) {
  const query = useQuery({
    queryKey: queryKeys.settings.all,
    queryFn: () => settingsApi.all(),
    staleTime: 5 * 60_000,
    /*
     * The read endpoint is guarded by `settings:write` — the only settings
     * permission there is — so a role without it must be able to say so rather
     * than fire a request that is certain to come back 403. Callers that read
     * branding for the whole workspace (the accent colour) pass `enabled`.
     */
    enabled: options?.enabled ?? true,
  });

  // The company's regime falls back to the country's own default rather than
  // to Argentina's. `RESPONSABLE_INSCRIPTO` was compiled in here, which meant
  // a workspace that had not filled the fiscal profile yet was described as
  // an Argentine taxpayer wherever this hook is read.
  const { profile } = useCountryProfile();

  const settings = query.data ?? [];

  const branding = useMemo<BrandingSetting>(() => {
    const entry = settings.find((setting) => setting.key === 'branding');
    return (entry?.value as BrandingSetting | undefined) ?? {};
  }, [settings]);

  const fiscal = useMemo<FiscalProfile>(() => {
    const entry = settings.find((setting) => setting.key === 'fiscal');
    return (entry?.value as FiscalProfile | undefined) ?? {};
  }, [settings]);

  const numbering = useMemo(() => {
    const entry = settings.find((setting) => setting.key === 'numbering');
    return (
      (entry?.value as Record<string, { prefix?: string; nextValue?: number }> | undefined) ?? {}
    );
  }, [settings]);

  const stock = useMemo(() => {
    const entry = settings.find((setting) => setting.key === 'stock');
    return (entry?.value as { allowNegative?: boolean } | undefined) ?? {};
  }, [settings]);

  return {
    settings,
    branding,
    fiscal,
    numbering,
    stock,
    currency: branding.currency ?? profile?.currency ?? DEFAULT_CURRENCY,
    displayName: branding.displayName,
    defaultLocale: branding.locale ?? DEFAULT_LOCALE,
    /** The company's IVA condition, which decides the class of a document. */
    ivaCondition: fiscal.ivaCondition ?? profile?.tax.defaultRegime ?? '',
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

/** The tenant's configured locale, used as the default for new users. */
export function useTenantLocale(): Locale {
  const { can } = useAuth();
  const { defaultLocale } = useTenantSettings({ enabled: can('settings:write') });
  return defaultLocale;
}
